/**
 * Single card in the Deal Recommendations history tab.
 * Shows the action label, any field-level applied_changes diff,
 * the signal type, and the timestamp.
 */
import { PiSparkle } from 'react-icons/pi'
import { format } from 'date-fns'
import type { RecommendationHistoryItem } from 'models/recommendationHistory'

// Mirrors recommendations.models.ActionType (server/recommendations/models.py). Unknown types fall back to the raw action_type string below.
const ACTION_LABELS: Record<string, string> = {
  update_stage: 'Updated stage',
  update_close_date: 'Updated close date',
  create_reminder: 'Created reminder',
  reschedule_reminder: 'Rescheduled reminder',
  delete_reminder: 'Deleted reminder',
  add_note: 'Added note',
  send_email: 'Sent email',
  create_contact: 'Created contact',
  update_contact: 'Updated contact',
}

function humanizeSignal(signal: string): string {
  return signal.replace(/_/g, ' ')
}

export function RecommendationEventCard({
  item,
}: {
  item: RecommendationHistoryItem
}) {
  const actionLabel = ACTION_LABELS[item.action_type] ?? item.action_type
  const time = format(new Date(item.clicked_at), 'MMM d, p')

  return (
    <div className="flex items-start gap-3">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full bg-green-50 flex items-center justify-center relative z-[2]"
        style={{ border: '1px solid rgba(21, 128, 61, 0.5)' }}
      >
        <PiSparkle className="text-green-700" size={18} />
      </div>

      <div className="flex-1 min-w-0 bg-white rounded-lg px-3 py-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-inter text-base text-gray-700 font-medium">
              {actionLabel}
            </div>

            {item.applied_changes?.map((c) => (
              <div key={c.field} className="font-inter text-base text-gray-500">
                {c.label}:{' '}
                <span className="text-gray-700">{c.from ?? '—'}</span>
                {' → '}
                <span className="text-gray-700">{c.to ?? '—'}</span>
              </div>
            ))}

            <div className="font-inter text-sm text-gray-400 mt-0.5">
              {humanizeSignal(item.signal_type)} · {item.status}
            </div>
          </div>

          <div className="flex-shrink-0">
            <span className="font-inter text-base text-right text-gray-500 tabular-nums">
              {time}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
